import React from "react";
import Marquee from "react-fast-marquee";
import { ShieldCheck, ArrowRight, Quote, BadgeCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";

export const GuaranteeStrip = () => (
  <div className="bg-[#040406] border-y border-white/10 py-4" data-testid="guarantee-marquee">
    <Marquee gradient={false} speed={45} autoFill>
      {["RESULT OR YOUR MONEY BACK", "JEE", "NEET", "SSC", "BANKING", "RAILWAY", "GOVT EXAMS"].map((t) => (
        <span key={t} className="mx-8 text-sm font-display font-semibold tracking-widest text-white/40 uppercase flex items-center gap-8">
          {t} <span className="text-amber-400">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

export const GuaranteeSection = ({ onReadTerms }) => (
  <section id="guarantee" className="relative overflow-hidden bg-[#040406] text-white py-24 md:py-32 scroll-mt-16" data-testid="guarantee-section">
    <div className="absolute inset-0 hero-radial opacity-70" />
    <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <Reveal>
        <div className="mx-auto h-20 w-20 rounded-3xl bg-gradient-to-br from-amber-400/20 to-red-600/20 border border-amber-400/30 flex items-center justify-center mb-8 glow-cyan">
          <ShieldCheck className="h-10 w-10 text-amber-300" />
        </div>
        <p className="text-xs uppercase tracking-[0.25em] text-amber-300 font-semibold mb-5">The sarkarijobai Success Guarantee</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">
          We put real skin in the game.
        </h2>
        <p className="mt-6 text-base sm:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
          If you complete the daily study time you commit to at the start, follow the required program,
          and still don't achieve the defined qualifying outcome, you may qualify for a refund.
        </p>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-12 inline-block rounded-3xl border border-amber-400/30 bg-gradient-to-b from-amber-500/10 to-transparent px-8 sm:px-16 py-10 glow-cyan">
          <p className="font-display text-3xl sm:text-5xl font-bold tracking-tight">
            RESULT OR YOUR <span className="text-gradient">MONEY BACK</span><sup className="text-amber-300 text-2xl">*</sup>
          </p>
        </div>
        <p className="mt-6 text-xs text-white/45 max-w-xl mx-auto leading-relaxed">
          *Eligibility, qualifying outcomes, usage requirements and refund conditions apply. See full terms.
        </p>
        <button
          onClick={onReadTerms}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-7 py-3.5 hover:scale-105 transition-transform"
          data-testid="guarantee-read-terms"
        >
          Read the Full Guarantee <ArrowRight className="h-4 w-4" />
        </button>
      </Reveal>
    </div>
  </section>
);

export const WhyGuarantee = () => (
  <section className="bg-background py-24 md:py-32" data-testid="why-guarantee-section">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
      <Reveal>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          Most EdTech sells access.
          <br />
          <span className="text-gradient">We sell commitment to the process.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-8 space-y-4 text-base text-muted-foreground leading-relaxed">
          <p>We can't sit your exam for you.</p>
          <p>We can't control the paper.</p>
          <p>We can't promise a government department will select you.</p>
          <p>
            What we can do is build a system around your preparation, measure whether you're following it,
            identify where you're falling behind, and continuously help you correct course.
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <h3 className="mt-10 font-display text-2xl sm:text-3xl font-bold tracking-tight">
          Your effort is yours. Your preparation system is ours.
        </h3>
      </Reveal>
    </div>
  </section>
);

export const SocialProof = () => (
  <section className="bg-[#040406] text-white py-24 md:py-32" data-testid="social-proof-section">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center max-w-2xl mx-auto mb-14">
        <p className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold mb-4">Verified results</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
          Real evidence, not manufactured hype.
        </h2>
        <p className="mt-5 text-white/50 leading-relaxed">
          We do not publish fake testimonials or invented success rates. Verified student results will
          appear here as they are confirmed.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-5">
        {[0, 1, 2].map((i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="h-full rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-6">
              <Quote className="h-6 w-6 text-white/20 mb-4" />
              <p className="text-sm text-white/50 italic mb-6">"Add verified student testimonial here."</p>
              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center text-white/30 text-xs">Photo</div>
                <div className="text-xs text-white/40 space-y-0.5">
                  <p>Exam · Placeholder</p>
                  <p>Start score → End score</p>
                  <p className="flex items-center gap-1 text-amber-300/70"><BadgeCheck className="h-3 w-3" /> Verified result</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {["Score improvements", "Selection results", "Percentile improvements", "Student stories"].map((t) => (
            <div key={t} className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-5 text-center">
              <p className="text-xs uppercase tracking-wide text-white/30">Space for</p>
              <p className="text-sm font-medium text-white/70 mt-1">{t}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);
